import React, { useEffect, useMemo, useState } from "react";
import {
    UserOutlined,
    ProfileOutlined,
    UnorderedListOutlined,
    PlayCircleOutlined,
    SendOutlined,
    SettingOutlined,
    SafetyCertificateOutlined,
    TrophyOutlined,
} from "@ant-design/icons";
import { Avatar, Badge, Layout, Menu, Space, Typography } from "antd";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Loading from "../Loading";
import { routeAdmin } from "../../pages/AdminPage";
import { routePromo } from "../../pages/PromoPage";
import { routeLogs } from "../../pages/LogsPage";
import { routeGames } from "../../pages/GamesPage";
import { routeMailing } from "../../pages/MailingPage";
import { routeAdminUsers } from "../../pages/AdminUsersPage";
import { routeJackpot } from "../../pages/JackpotPage";

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const PageWrapper = ({ children }) => {
    const history = useHistory();
    const location = useLocation();
    const loading = useSelector((state) => state.loading);
    const role = useSelector((state) => state?.profileData?.role);

    const items = useMemo(() => {
        const isSuperAdmin = Number(role) === 1;

        const base = [
            { label: "Telegram пользователи", key: routeAdmin(), icon: <UserOutlined /> },
            { label: "Джекпоты", key: routeJackpot(), icon: <TrophyOutlined /> },
        ];

        const superAdminOnly = [
            { label: "Промоакции", key: routePromo(), icon: <ProfileOutlined /> },
            { label: "Игры", key: routeGames(), icon: <PlayCircleOutlined /> },
            { label: "Рассылка", key: routeMailing(), icon: <SendOutlined /> },
            { label: "Администраторы", key: routeAdminUsers(), icon: <SettingOutlined /> },
            { label: "Логи действий", key: routeLogs(), icon: <UnorderedListOutlined /> },
        ];

        return isSuperAdmin ? [...base, ...superAdminOnly] : base;
    }, [role]);

    const [current, setCurrent] = useState(location.pathname || routeAdmin());

    useEffect(() => {
        setCurrent(location.pathname || routeAdmin());
    }, [location.pathname]);

    useEffect(() => {
        const allowedKeys = new Set(items.map((i) => i.key));
        if (!allowedKeys.has(current)) {
            const fallback = routeAdmin();
            setCurrent(fallback);
            if (location.pathname !== fallback) history.replace(fallback);
        }
    }, [items, current, history, location.pathname]);

    const onClick = (e) => {
        setCurrent(e.key);
        history.push(e.key);
    };

    return (
        <div className={"admin-inner"}>
            {loading && <Loading loading={loading} />}

            <style>
                {`
          .admin-menu.ant-menu-horizontal {
            border-bottom: none !important;
          }

          .admin-menu.ant-menu-horizontal > .ant-menu-item::after,
          .admin-menu.ant-menu-horizontal > .ant-menu-submenu::after {
            border-bottom: none !important;
          }

          .admin-menu.ant-menu-horizontal > .ant-menu-item-selected::after,
          .admin-menu.ant-menu-horizontal > .ant-menu-submenu-selected::after {
            border-bottom: none !important;
          }

          .admin-menu.ant-menu-horizontal > .ant-menu-item-selected,
          .admin-menu.ant-menu-horizontal > .ant-menu-submenu-selected {
            background: transparent !important;
          }

          .admin-menu.ant-menu-horizontal > .ant-menu-item:active,
          .admin-menu.ant-menu-horizontal > .ant-menu-submenu-title:active {
            background: transparent !important;
          }

          .admin-menu.ant-menu-horizontal > .ant-menu-item:focus,
          .admin-menu.ant-menu-horizontal > .ant-menu-item:focus-visible,
          .admin-menu.ant-menu-horizontal > .ant-menu-submenu-title:focus,
          .admin-menu.ant-menu-horizontal > .ant-menu-submenu-title:focus-visible {
            outline: none !important;
            box-shadow: none !important;
          }
        `}
            </style>

            <Layout style={{ background: "transparent" }}>
                <Header
                    style={{
                        background:
                            "linear-gradient(135deg, rgba(22, 119, 255, 0.16) 0%, rgba(114, 46, 209, 0.14) 55%, rgba(22, 119, 255, 0.10) 100%)",
                        border: "1px solid rgba(255, 255, 255, 0.10)",
                        borderRadius: "0px 0px 16px 16px",
                        height: "auto",
                        lineHeight: "normal",
                        padding: 16,
                        marginBottom: 12,
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 16,
                        }}
                    >
                        <Space size={14} align="center" style={{ minWidth: 260, flexShrink: 0 }}>
                            <Badge dot offset={[-2, 34]} color="#52c41a">
                                <Avatar
                                    size={52}
                                    icon={<SafetyCertificateOutlined />}
                                    style={{
                                        background: "linear-gradient(135deg, #1677ff 0%, #722ed1 100%)",
                                        boxShadow: "0 10px 24px rgba(0, 0, 0, 0.20)",
                                    }}
                                />
                            </Badge>

                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Title level={4} style={{ margin: 0, letterSpacing: 0.2 }}>
                                    Admin Console
                                </Title>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    Панель управления Telegram Mini App
                                </Text>
                            </div>
                        </Space>

                        <div
                            style={{
                                padding: "10px 12px",
                                borderRadius: 14,
                                background: "rgba(255, 255, 255, 0.06)",
                                border: 0,
                                width: "100%",
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Menu
                                className="admin-menu"
                                onClick={onClick}
                                selectedKeys={[current]}
                                mode="horizontal"
                                items={items}
                                style={{
                                    background: "transparent",
                                    border: 0,
                                    width: "100%",
                                    justifyContent: "flex-end",
                                    fontWeight: 600,
                                }}
                            />
                        </div>
                    </div>
                </Header>

                <Content style={{ background: "transparent" }}>{children}</Content>
            </Layout>
        </div>
    );
};

export default PageWrapper;